import React, { useEffect, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Chip,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Select,
	SelectChangeEvent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';

import { dashIfNone, formatDateTimeToShowUser } from 'features/common/helpers';
import {
	IChannel,
	IPatchSaleOrder,
	ISaleOrderDetail,
	ITransaction,
	SegmentEnum,
	segmentTypes,
	TypeServiceEnum,
} from 'features/salesOrder/types';
import {
	getChannels,
	getPaymentReceivedTransactions,
	getVariationLogs,
	patchSaleOrder,
} from 'features/salesOrder/services';
import { Link, useParams } from 'react-router-dom';
import { ShowAlertState } from 'features/common/types';
import { generateSalesOrderUrl } from '../../helpers';
import { parseAgencyIdFromSearch } from '../../helpers/saleOrderAgencyContext';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HistoryIcon from '@mui/icons-material/History';
import SectionCard from './SectionCard';
import CobrosCard from './CobrosCard';
import { useSaleOrderInvoicing } from 'features/invoices/components/SaleOrderInvoices/InvoicingContext';
import { clientDisplayName } from 'features/invoices/components/SaleOrderInvoices/CreditLimitBanner';
import { CashIcon, TransferIcon } from '../../../../../features/common/customIcons';
import { CountryCodeEnum, orderTypes } from '../../../../../features/common/types';
// TODO: reactivar junto con bloque Sincro NetSuite
// import NetsuiteEntitySyncStatus from '../../../Netsuite/components/NetsuiteEntitySyncStatus';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { isSuperAdmin } from '../../../../../features/auth/helpers';
import DatosDeLaVenta from './DatosDeLaVenta';
import SaleOrderPendingPayMessageSchedule from '../SaleOrderPendingPayMessageSchedule';
import DetalleCobroAerolineas from './DetalleCobroAerolineas';
import routes from '../../../../../constants/routes';
import bancard from '../../../../../assets/payment-processors/bancard.svg';
import { OperationalRequests } from '../OperationalRequests';
import ChangeSaleOrderSellerModal from '../ChangeSaleOrderSellerModal';
import { getAgencyIdOrDefault } from '../../../../../features/auth/constants';

interface Props {
	data: ISaleOrderDetail;
	onSetAlert: (values: ShowAlertState) => void;
	onUpdate: () => void;
	setLoading: (loading: boolean) => void;
	loading: boolean;
}

const formatDate = (value: string) => {
	// "2024-04-18"
	const arr = value.split('-');
	return `${arr[2]}/${arr[1]}/${arr[0]}`;
};

export interface LogItem {
	id: string;
	saleOrderId: string;
	saleAmount: number;
	saleAmountVariation: number;
	totalCost: number;
	totalCostVariation: number;
	grossProfit: number;
	grossProfitVariation: number;
	dateCreated: string;
}

// Helper function para obtener passengerIds según el tipo de servicio
const getPassengerIds = (service: any): string[] => {
	// @ts-ignore
	switch (service.serviceType) {
		case TypeServiceEnum.Accommodation: {
			// Para alojamiento, los pasajeros están en las habitaciones
			const allPassengerIds: string[] = [];
			if (service.rooms && Array.isArray(service.rooms)) {
				service.rooms.forEach((room: any) => {
					if (room.passengerIds && Array.isArray(room.passengerIds)) {
						allPassengerIds.push(...room.passengerIds);
					}
				});
			}
			return Array.from(new Set(allPassengerIds)); // Eliminar duplicados
		}
		case TypeServiceEnum.Flight: {
			// Para vuelos, los pasajeros están en passengersGroupByPassengerType
			const allPassengerIds: string[] = [];
			if (service.passengersGroupByPassengerType && Array.isArray(service.passengersGroupByPassengerType)) {
				service.passengersGroupByPassengerType.forEach((group: any) => {
					if (group.passengerIds && Array.isArray(group.passengerIds)) {
						allPassengerIds.push(...group.passengerIds);
					}
				});
			}
			return Array.from(new Set(allPassengerIds)); // Eliminar duplicados
		}
		case TypeServiceEnum.FlightAncillary: {
			// Para FlightAncillary, usar passengerIds directamente
			return service.passengerIds || [];
		}
		default: {
			// Para otros tipos de servicios, usar passengerIds directamente
			return service.passengerIds || [];
		}
	}
};

const getPassengerNames = (service: any, passengers: any[]): string[] => {
	const passengerIds = getPassengerIds(service);
	return passengerIds
		.map((id) => {
			const passenger = passengers.find((p) => p.keyId === id);
			return passenger ? `${passenger.name} ${passenger.lastName}` : null;
		})
		.filter(Boolean) as string[];
};

const getServiceTooltipContent = (service: any, passengers: any[]): JSX.Element => {
	const fullDescription = getServiceDescription(service);
	const passengerNames = getPassengerNames(service, passengers);

	return (
		<Box>
			<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
				{fullDescription}
			</Typography>
			{passengerNames.length > 0 && (
				<Box>
					<Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
						Pasajeros:
					</Typography>
					{passengerNames.map((name, index) => (
						<Typography key={index} variant="caption" sx={{ display: 'block', ml: 1 }}>
							• {name}
						</Typography>
					))}
				</Box>
			)}
		</Box>
	);
};

// @ts-ignore
const getServiceDescription = (service) => {
	if (!service || !service.serviceType) {
		return 'No especificado';
	}
	let description;
	switch (service.serviceType) {
		case TypeServiceEnum.Accommodation: {
			const destinationCode = service.destination?.code ?? 'N/A';
			const serviceProviderName = service.serviceProvider?.name ?? 'N/A';
			const roomsLength = service.rooms?.length ?? 0;
			description = `Alojamiento | ${destinationCode}: ${serviceProviderName} | ${roomsLength} hab`;
			break;
		}
		case TypeServiceEnum.Flight: {
			const vcCode = service.vc?.code ?? 'N/A';
			const reference = service.reference ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `Vuelo | vc: ${vcCode} | pnr: ${reference} | ${passengerIdsLength} pax`;
			break;
		}
		case TypeServiceEnum.FlightAncillary: {
			const flightAncillaryType = service.flightAncillaryType ?? 'N/A';
			const vcCode = service.vc?.code ?? 'N/A';
			const reference = service.reference ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `FlightAncillary | ${flightAncillaryType} | vc: ${vcCode} | pnr: ${reference} | ${passengerIdsLength} pax`;
			break;
		}
		case TypeServiceEnum.Other: {
			const otherServiceType = service.otherServiceType ?? 'Otro';
			const serviceName = service.serviceName ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `${otherServiceType}: ${serviceName} | ${passengerIdsLength} pax`;
			break;
		}
		case TypeServiceEnum.Transfer: {
			const transferSegment = service.transferSegments?.[0];
			const transferType = transferSegment?.transferType ?? 'N/A';
			const originName = transferSegment?.originName ?? 'N/A';
			const destinationName = transferSegment?.destinationName ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `Transfer | ${transferType} | ${originName} → ${destinationName} | ${passengerIdsLength} pax`;
			break;
		}
		default: {
			description = 'Tipo de servicio no reconocido';
			break;
		}
	}
	return description;
};

// @ts-ignore
const capitalizeWords = (str) => {
	// @ts-ignore
	return str ? str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) : '';
};

export const General = ({ data, onSetAlert, onUpdate, setLoading }: Props): JSX.Element => {
	const { id: saleOrderId } = useParams<{ id: string }>();
	const [variationData, setVariationData] = useState<LogItem[]>([]);
	const [channels, setChannels] = useState<IChannel[]>([]);
	const [transactions, setTransactions] = useState<ITransaction[]>([]);
	const [loadingTransactions, setLoadingTransactions] = useState<boolean>(true);
	const [transactionsError, setTransactionsError] = useState<string | null>(null);
	const [isEditingClientReference, setIsEditingClientReference] = useState(false);
	const [clientReference, setClientReference] = useState(data.clientReference || '');
	const clientReferenceTextFieldRef = useRef(null);
	const [openChangeSellerModal, setOpenChangeSellerModal] = useState(false);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

	/** Billing settings de los clientes facturables (via provider, lazy). */
	const { invoiceableClients } = useSaleOrderInvoicing();
	const clientsRequiringPurchaseOrder = invoiceableClients.filter((c) => c.requiresPurchaseOrder);

	// Función para navegar al servicio específico
	const handleServiceClick = (serviceKeyId: string) => {
		const agencyId = parseAgencyIdFromSearch(window.location.search);
		const newUrl = generateSalesOrderUrl(saleOrderId, serviceKeyId, agencyId);
		window.history.replaceState(null, '', newUrl);
		// Disparar un evento personalizado para que el componente padre actualice la vista
		window.dispatchEvent(new CustomEvent('navigate-to-service', { detail: { serviceId: serviceKeyId } }));
	};

	// Estado para el menú de pagos
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	useEffect(() => {
		const fetchLogs = async () => {
			try {
				const { data: result } = await getVariationLogs(saleOrderId);
				setVariationData(result);
			} catch (e) {
				console.log(e);
			}
		};

		fetchLogs();
	}, []);

	useEffect(() => {
		const fetchTransactions = async () => {
			try {
				const { data: transactionsData } = await getPaymentReceivedTransactions(saleOrderId);
				setTransactions(transactionsData);
			} catch (e) {
				setTransactionsError('Error fetching transactions');
			} finally {
				setLoadingTransactions(false);
			}
		};

		fetchTransactions();
	}, [saleOrderId]);

	const mainPassenger = data.passengers.find((passenger) => passenger.mainPassenger);
	const nameAndLastName = mainPassenger ? `${mainPassenger.name} ${mainPassenger.lastName}` : 'No definido';
	const passengerCount = data?.passengers?.length ?? 0;
	const services = data.services;

	const handleChangeSegmentType = (event: SelectChangeEvent) => {
		handleRequest({
			segmentType: event.target.value as string,
		});
	};

	const handleChangeChannel = (event: SelectChangeEvent) => {
		handleRequest({
			channelId: Number(event.target.value),
		});
	};

	const handleRequest = async (data: IPatchSaleOrder) => {
		try {
			setLoading(true);
			await patchSaleOrder(saleOrderId, data);
			onSetAlert({
				show: true,
				severity: 'success',
				message: 'Salesorder actualizada',
			});
			onUpdate();
		} catch (e) {
			// @ts-ignore
			alert(e.response.data.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const loadChannels = async () => {
			try {
				const response = await getChannels();
				setChannels(response.data);
			} catch (e: any) {
				console.error(e);
			}
		};

		loadChannels();
	}, []);

	const handleChangeClientReference = (event: React.ChangeEvent<HTMLInputElement>) => {
		setClientReference(event.target.value);
	};

	const handleSaveClientReference = async () => {
		setIsEditingClientReference(false);
		await handleRequest({ clientReference: clientReference });
	};

	const handleEditClientReferenceClick = () => {
		setIsEditingClientReference(true);
		setTimeout(() => {
			if (clientReferenceTextFieldRef && clientReferenceTextFieldRef.current) {
				// @ts-ignore
				clientReferenceTextFieldRef?.current?.focus();
			}
		}, 100);
	};

	const handleKeyPressClientReferenceField = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && (data.clientReference || '') !== clientReference) {
			e.preventDefault();
			handleSaveClientReference();
		}
	};

	// TODO: reactivar junto con bloque Sincro NetSuite
	// const handleAddToQueueToSyncWithNetsuite = async () => {
	// 	try {
	// 		if (loading) return;
	// 		setLoading(true);
	// 		await addToQueueToSyncWithNetsuite(saleOrderId);
	// 		onSetAlert({
	// 			show: true,
	// 			severity: 'success',
	// 			message: 'Salesorder agregada a cola de sincronización con Netsuite satisfactorimente',
	// 		});
	// 		onUpdate();
	// 	} catch (error) {
	// 		onSetAlert({
	// 			show: true,
	// 			severity: 'error',
	// 			message: extractErrorMessage(
	// 				error,
	// 				'Ocurrió un error al agregar la salesorder a la cola de sincronización con Netsuite',
	// 			),
	// 		});
	// 	} finally {
	// 		setLoading(false);
	// 	}
	// };

	const { auth } = useSelector((state: RootState) => state);

	// Verificar si el usuario está en Uruguay (para mostrar el componente solo a usuarios de Uruguay)
	const isUruguayUser = auth.country === 'UY';

	const agencyIdOfMarketCountry = getAgencyIdOrDefault(auth.country);
	const isSuperAdminResult = isSuperAdmin(auth.user);

	// TODO: reactivar junto con bloque Sincro NetSuite
	// const [modalOpen, setModalOpen] = useState(false);
	// const [switchChecked, setSwitchChecked] = useState(data.automaticSyncWithNetsuite);
	//
	// const handleOpenModal = () => {
	// 	setModalOpen(true);
	// };
	//
	// const handleCloseModal = () => {
	// 	setModalOpen(false);
	// 	if (!switchChecked) {
	// 		setSwitchChecked(true);
	// 	}
	// };
	//
	// const handleConfirmModal = () => {
	// 	setSwitchChecked(false);
	// 	handleSyncChange(false);
	// 	handleCloseModal();
	// };
	//
	// const handleSyncChange = async (isEnabled: boolean) => {
	// 	await handleRequest({ automaticSyncWithNetsuite: isEnabled });
	// };
	//
	// const handleChangeAutomaticSyncWithNetsuite = async (event: { target: { checked: any } }) => {
	// 	const { checked } = event.target;
	// 	if (!checked) {
	// 		handleOpenModal();
	// 	} else {
	// 		setSwitchChecked(true);
	// 		handleSyncChange(true);
	// 	}
	// };

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handlePaymentOptionClick = (route: string): void => {
		if (route === routes.payment.wireTransferCheck) {
			window.open(route + `?saleOrderId=${saleOrderId}`, '_blank');
		} else {
			const params = new URLSearchParams();
			params.append('saleOrderId', saleOrderId);

			// Agregamos el deal o caso según corresponda
			if (data.isBitrixDeal) {
				params.append('dealId', data.dealId.toString());
			} else {
				params.append('caseId', data.dealId.toString());
			}

			window.open(`${route}?${params.toString()}`, '_blank');
		}
		handleClose();
	};

	const orderTypesAllowed = orderTypes.filter((x) => x.countriesAllow.indexOf(auth.country) > -1);

	const datosDeLaVentaProps = {
		saleAmount: data.saleAmount,
		totalCost: data.totalCost,
		grossProfitPercentage: data.grossProfitPercentage,
		grossProfit: data.grossProfit,
		chargedAmount: data.chargedAmount,
		pendingPayAmount: data.pendingPayAmount,
		travelDepartureDate: data.travelDepartureDate,
		currency: data.currency,
	};

	const stretchCardSx = { display: 'flex', flexDirection: 'column', height: '100%' };

	return (
		<Box
			px={isMobile ? 2 : 3}
			mt={2}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
			}}
		>
			{/* Cards superiores — grid en desktop para igualar alturas */}
			<Box
				sx={{
					display: isTablet ? 'flex' : 'grid',
					flexDirection: isTablet ? (isMobile ? 'column' : 'row') : undefined,
					gridTemplateColumns: isTablet ? undefined : '2fr 1fr 350px',
					gap: 2,
					alignItems: 'stretch',
				}}
			>
				{/* Card Orden de ventas */}
				<SectionCard
					title="Orden de ventas"
					icon={<AssignmentOutlinedIcon fontSize="small" />}
					sx={{ ...(isTablet ? { flex: isMobile ? '1' : '2' } : {}), ...stretchCardSx }}
				>
					<Box display="flex" gap={1} flexDirection={isSmallMobile ? 'column' : 'row'}>
						<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '100px' }}>
							Creada:
						</Typography>

						<Box>
							<Typography variant="body2" component="span">
								{data.created ? formatDateTimeToShowUser(data.created).substring(0, 10) : '-'}
							</Typography>

							<Typography variant="body2" component="span" style={{ fontWeight: 'bold', marginLeft: 8 }}>
								por {data.createdBy}
							</Typography>

							{isSuperAdminResult && (
								<Tooltip title="Cambiar vendedor">
									<IconButton color="primary" onClick={() => setOpenChangeSellerModal(true)} sx={{ ml: 0.5, p: 0.5 }}>
										<EditIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					</Box>

					<Box display="flex" gap={1} flexDirection={isSmallMobile ? 'column' : 'row'}>
						<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '100px' }}>
							Modificada:
						</Typography>
						<Box>
							<Typography variant="body2" component="span">
								{data.updated ? formatDateTimeToShowUser(data.updated).substring(0, 10) : ''}
							</Typography>
							<Typography variant="body2" component="span" style={{ fontWeight: 'bold', marginLeft: 8 }}>
								{data.updatedBy ? `por ${data.updatedBy}` : ''}
							</Typography>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: isSmallMobile ? 'column' : 'row',
							alignItems: isSmallMobile ? 'stretch' : 'center',
							gap: 1,
						}}
					>
						<Tooltip title='LEISURE: todas las oórdes exceptuando aquellas que que provienen de clientes de tipo empresa que CUENTAN CON UN ACUERDO CORPORATIVO ACTIVO. Estas ultimas podran ser de tipo CORPO o MAYORISTA. Nota: una orden para las vacaciones de "Pepe" que es de la empresa "PEPITO S.A." y que no será facturada a la misma porque se trata de una compra personal, es una orden de tipo "LEISURE"'>
							<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '100px' }}>
								Segmento:
							</Typography>
						</Tooltip>
						<Select
							fullWidth
							size="small"
							value={segmentTypes.find((st) => st.id === data.segmentType)?.id || ''}
							onChange={handleChangeSegmentType}
						>
							{segmentTypes.map((segmentType) => {
								return (
									<MenuItem key={segmentType.id} value={segmentType.id}>
										{segmentType.name}
									</MenuItem>
								);
							})}
						</Select>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: isSmallMobile ? 'column' : 'row',
							alignItems: isSmallMobile ? 'stretch' : 'center',
							gap: 1,
						}}
					>
						<Tooltip title="El canal es definido en base a como llegó esta venta a nosotros. LEAD: por default es nuestro canal, las ordenes originadas por un cliente potencial que mostró interés mediante una campaña o contacto inicial. | REFERIDO: para las ordenes provenientes de una recomendación de clientes actuales, socios comerciales, un amigo, familia, etc. | FULL-ONLINE: Ordenes originadas por ventas en la web. | OFICINA: Aquellas ordenes priginadas pro clientes que entraron a nuestras oficinas a consultarnos y compraron.">
							<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '100px' }}>
								Canal:
							</Typography>
						</Tooltip>
						<Select fullWidth size="small" value={data.channel.id.toString()} onChange={handleChangeChannel}>
							{channels.map((channel) => {
								return (
									<MenuItem key={channel.id} value={channel.id}>
										{channel.name}
									</MenuItem>
								);
							})}
						</Select>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: isSmallMobile ? 'column' : 'row',
							alignItems: isSmallMobile ? 'stretch' : 'center',
							gap: 1,
						}}
					>
						<Tooltip title="Este campo es mayormente utilizado para las ordenes del segmento corporativo o mayorista. Aquí colocamos la referencia provista por nuestros cleintes, por ejemplo su identificador de venta o su orden de compra">
							<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '115px' }}>
								Referencia de cliente:
							</Typography>
						</Tooltip>
						<TextField
							fullWidth
							onKeyPress={handleKeyPressClientReferenceField}
							inputRef={clientReferenceTextFieldRef}
							disabled={!isEditingClientReference}
							value={clientReference}
							size="small"
							onChange={handleChangeClientReference}
							inputProps={{ maxLength: 45 }}
						/>
						<IconButton
							color="primary"
							disabled={isEditingClientReference && (data.clientReference || '') === clientReference}
							onClick={isEditingClientReference ? handleSaveClientReference : handleEditClientReferenceClick}
						>
							{isEditingClientReference ? <SaveIcon /> : <EditIcon />}
						</IconButton>
					</Box>

					{/* Clientes con "necesita OC": sin referencia cargada no se debería facturar (no aplica a Leisure) */}
					{data.segmentType !== SegmentEnum.LEISURE &&
						!clientReference.trim() &&
						clientsRequiringPurchaseOrder.length > 0 && (
							<Alert severity="error" variant="filled" sx={{ mt: 1 }}>
								{clientsRequiringPurchaseOrder.map(clientDisplayName).join(', ')} requiere orden de compra para facturar
								— completá la Referencia de cliente.
							</Alert>
						)}

					<Box display="flex" gap={1} flexDirection={isSmallMobile ? 'column' : 'row'}>
						<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '100px' }}>
							Pax principal:
						</Typography>
						<Typography variant="body2">{capitalizeWords(nameAndLastName)}</Typography>
					</Box>
				</SectionCard>

				{/* Detalle del viaje */}
				<SectionCard
					title="Detalle del viaje"
					icon={<FlightTakeoffIcon fontSize="small" />}
					sx={{ ...(isTablet ? { flex: isMobile ? '1' : '1' } : {}), ...stretchCardSx }}
				>
					<Box display="flex" gap={1} flexDirection={isSmallMobile ? 'column' : 'row'}>
						<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '120px' }}>
							Destino/s:
						</Typography>
						<Typography variant="body2">{dashIfNone(data.destinations)}</Typography>
					</Box>
					<Box display="flex" gap={1} flexDirection={isSmallMobile ? 'column' : 'row'}>
						<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '120px' }}>
							Fecha de comienzo:
						</Typography>
						<Typography variant="body2">
							{data.travelDepartureDate ? formatDate(data.travelDepartureDate) : '-'}
						</Typography>
					</Box>
					<Box display="flex" gap={1} flexDirection={isSmallMobile ? 'column' : 'row'}>
						<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '120px' }}>
							Fecha de finalización:
						</Typography>
						<Typography variant="body2">{data.travelReturnDate ? formatDate(data.travelReturnDate) : '-'}</Typography>
					</Box>
					<Box display="flex" gap={1} flexDirection={isSmallMobile ? 'column' : 'row'}>
						<Typography variant="body2" sx={{ minWidth: isSmallMobile ? 'auto' : '120px' }}>
							Cantidad de pax:
						</Typography>
						<Typography variant="body2">{passengerCount}</Typography>
					</Box>
				</SectionCard>

				{/* TODO: Bloque Sincro NetSuite deshabilitado — reactivar cuando refactoricemos facturación
				<Card elevation={4}>
					...
				</Card>
				*/}

				{!isTablet && <DatosDeLaVenta {...datosDeLaVentaProps} />}
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: isTablet ? 'column' : 'row',
					gap: 2,
				}}
			>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					{isTablet && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
							<DatosDeLaVenta {...datosDeLaVentaProps} />
							<CobrosCard transactions={transactions} loading={loadingTransactions} error={transactionsError} dense />
							<SaleOrderPendingPayMessageSchedule
								saleOrderId={saleOrderId}
								segmentType={data.segmentType}
								onSetAlert={onSetAlert}
							/>
						</Box>
					)}

					{/* Tabla de servicios */}
					<Box mb={2}>
						<SectionCard
							title="Servicios de la orden"
							icon={<ListAltIcon fontSize="small" />}
							action={
								<Link
									to={(() => {
										const base = routes.salesOrder.resumeByPax.replace(':id', saleOrderId || '');
										const agencyId = parseAgencyIdFromSearch(window.location.search);
										return agencyId ? `${base}?agencyId=${agencyId}` : base;
									})()}
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: 'none', color: 'inherit' }}
								>
									<Typography
										variant="body2"
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 0.5,
											color: 'primary.main',
											'&:hover': {
												textDecoration: 'underline',
											},
										}}
									>
										👥 detalle x pax
									</Typography>
								</Link>
							}
						>
							<Box sx={{ overflowX: 'auto' }}>
								<Table sx={{ minWidth: isMobile ? 320 : 650, tableLayout: 'fixed' }} aria-label="simple table">
									<TableHead>
										<TableRow>
											<TableCell
												sx={{
													fontSize: isMobile ? '0.75rem' : '0.875rem',
													width: isMobile ? '40%' : '45%',
												}}
											>
												Tipo de Servicio
											</TableCell>
											<TableCell
												sx={{
													fontSize: isMobile ? '0.75rem' : '0.875rem',
													width: isMobile ? '20%' : '18%',
													textAlign: 'right',
												}}
											>
												Precio de venta
											</TableCell>
											<TableCell
												sx={{
													fontSize: isMobile ? '0.75rem' : '0.875rem',
													width: isMobile ? '20%' : '18%',
													textAlign: 'right',
												}}
											>
												Costo
											</TableCell>
											<TableCell
												sx={{
													fontSize: isMobile ? '0.75rem' : '0.875rem',
													width: isMobile ? '20%' : '19%',
													textAlign: 'right',
												}}
											>
												Margen bruto
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{services.map((service, index) => {
											const isInactive = service && service.enable === false;
											const totalSaleAmount = parseFloat((service as any).totalSaleAmount) || 0;
											const totalCost = parseFloat((service as any).totalCost) || 0;
											const grossMargin = totalSaleAmount - totalCost;
											const isNegativeMargin = grossMargin < 0;
											const currency = (service as any).currency || '';

											// Helper function to format numbers with 2 decimals
											const formatAmount = (amount: number) => amount.toFixed(2);

											return (
												<TableRow
													key={index}
													sx={{
														backgroundColor: isInactive ? '#ffebee' : 'transparent',
														'&:hover': {
															backgroundColor: isInactive ? '#ffcdd2' : 'rgba(0, 0, 0, 0.04)',
														},
													}}
												>
													<Tooltip
														title={getServiceTooltipContent(service, data.passengers)}
														placement="top"
														arrow
														enterDelay={500}
													>
														<TableCell
															onClick={() => service?.keyId && handleServiceClick(service.keyId)}
															sx={{
																cursor: service?.keyId ? 'pointer' : 'default',
																padding: isMobile ? '8px' : '16px',
															}}
														>
															<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
																<Typography
																	color={service?.keyId ? 'primary' : 'textPrimary'}
																	sx={{
																		textDecoration: service?.keyId ? 'underline' : 'none',
																		textDecorationColor: 'transparent',
																		'&:hover': {
																			textDecorationColor: service?.keyId ? 'primary.main' : 'transparent',
																		},
																		fontSize: isMobile ? '0.75rem' : '0.875rem',
																		lineHeight: isMobile ? 1.2 : 1.43,
																	}}
																>
																	{service ? getServiceDescription(service) : 'Descripción no disponible'}
																</Typography>
																{isInactive && (
																	<Chip
																		label="Inactivo"
																		size="small"
																		color="error"
																		variant="outlined"
																		sx={{
																			height: '18px',
																			fontSize: '0.65rem',
																			fontWeight: 'bold',
																			alignSelf: 'flex-start',
																			'& .MuiChip-label': {
																				padding: '0 6px',
																			},
																		}}
																	/>
																)}
															</Box>
														</TableCell>
													</Tooltip>
													<TableCell
														sx={{
															fontSize: isMobile ? '0.75rem' : '0.875rem',
															textAlign: 'right',
														}}
													>
														{(service as any).totalSaleAmount !== undefined
															? `${currency} ${formatAmount(totalSaleAmount)}`
															: 'No especificado'}
													</TableCell>
													<TableCell
														sx={{
															fontSize: isMobile ? '0.75rem' : '0.875rem',
															textAlign: 'right',
														}}
													>
														{(service as any).totalCost !== undefined
															? `${currency} ${formatAmount(totalCost)}`
															: 'No especificado'}
													</TableCell>
													<TableCell
														sx={{
															fontSize: isMobile ? '0.75rem' : '0.875rem',
															textAlign: 'right',
														}}
													>
														<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
															<Typography
																sx={{
																	fontSize: isMobile ? '0.75rem' : '0.875rem',
																	fontWeight: isNegativeMargin ? 'bold' : 'normal',
																	color: isNegativeMargin ? '#d32f2f' : 'inherit',
																}}
															>
																{currency} {formatAmount(grossMargin)}
															</Typography>
															{isNegativeMargin && (
																<Chip
																	label="¡Pérdida!"
																	size="small"
																	color="error"
																	sx={{
																		height: '18px',
																		fontSize: '0.65rem',
																		fontWeight: 'bold',
																		backgroundColor: '#d32f2f',
																		color: 'white',
																		'& .MuiChip-label': {
																			padding: '0 6px',
																		},
																	}}
																/>
															)}
														</Box>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</Box>
						</SectionCard>
					</Box>

					{/* Solicitudes operativas */}
					<OperationalRequests
						services={services}
						transactions={transactions}
						passengers={data.passengers}
						saleOrderId={saleOrderId}
						onSetAlert={onSetAlert}
					/>

					{/* Tabla de modificaciones */}
					<Box>
						<SectionCard title="Modificaciones" icon={<HistoryIcon fontSize="small" />}>
							<Box sx={{ overflowX: 'auto' }}>
								<Table sx={{ minWidth: isMobile ? 320 : 650 }} aria-label="simple table">
									<TableHead>
										<TableRow>
											<TableCell align="left" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
												Fecha
											</TableCell>
											<TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
												Venta
											</TableCell>
											<TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
												modi (V)
											</TableCell>
											<TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
												Costo
											</TableCell>
											<TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
												modi (C)
											</TableCell>
											<TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
												Margen
											</TableCell>
											<TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
												modi (M)
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{variationData.map((row, index) => (
											<TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
												<TableCell align="left">
													<Typography variant="subtitle2" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
														{formatDate(row.dateCreated)}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<Typography variant="subtitle2" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
														USD {row.saleAmount}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<Typography variant="subtitle2" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
														USD {row.saleAmountVariation}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<Typography variant="subtitle2" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
														USD {row.totalCost}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<Typography variant="subtitle2" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
														USD {row.totalCostVariation}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<Typography variant="subtitle2" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
														USD {row.grossProfit}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<Typography variant="subtitle2" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
														USD {row.grossProfitVariation}
													</Typography>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</SectionCard>
					</Box>
				</Box>

				{/* Sidebar derecha - Solo en desktop grande */}
				{!isTablet && (
					<Box sx={{ flex: '0 0 350px', maxWidth: '350px' }}>
						<CobrosCard
							transactions={transactions}
							loading={loadingTransactions}
							error={transactionsError}
							action={
								<IconButton size="small" onClick={handleClick} color="primary">
									<AddIcon />
								</IconButton>
							}
						>
							<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
								{auth.country === CountryCodeEnum.TN_ITAU_PY ? (
									<MenuItem onClick={() => handlePaymentOptionClick(routes.payment.orderNaranja)}>
										<ListItemIcon>
											<img src={bancard} style={{ height: 30 }} />
										</ListItemIcon>
										<Typography>BanCard</Typography>
									</MenuItem>
								) : (
									orderTypesAllowed.map((order) => (
										<MenuItem
											key={`order-${order.id}`}
											onClick={() => handlePaymentOptionClick(routes.payment.order.replace(':id', `${order.id}`))}
										>
											<ListItemIcon>{order.icon()}</ListItemIcon>
											<Typography>{order.name}</Typography>
										</MenuItem>
									))
								)}
								<MenuItem onClick={() => handlePaymentOptionClick(routes.payment.wireTransferCheck)}>
									<ListItemIcon>
										<TransferIcon />
									</ListItemIcon>
									<Typography>WireTransferCheck</Typography>
								</MenuItem>
								<MenuItem onClick={() => handlePaymentOptionClick(routes.payment.orderCash)}>
									<ListItemIcon>
										<CashIcon />
									</ListItemIcon>
									<Typography>CashPayment</Typography>
								</MenuItem>
							</Menu>
						</CobrosCard>
						{isUruguayUser && (
							<Box mt={2}>
								<DetalleCobroAerolineas saleOrderId={saleOrderId} />
							</Box>
						)}
						<SaleOrderPendingPayMessageSchedule
							saleOrderId={saleOrderId}
							segmentType={data.segmentType}
							onSetAlert={onSetAlert}
						/>
					</Box>
				)}
			</Box>

			{/* Datos de la venta y cobros - En tablet van al final */}
			{isTablet && !isMobile && (
				<Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
					<Box sx={{ flex: 1 }}>
						<DatosDeLaVenta {...datosDeLaVentaProps} />
					</Box>
					<Box sx={{ flex: 1 }}>
						<CobrosCard transactions={transactions} loading={loadingTransactions} error={transactionsError} />
						{isUruguayUser && (
							<Box mt={2}>
								<DetalleCobroAerolineas saleOrderId={saleOrderId} />
							</Box>
						)}
						<SaleOrderPendingPayMessageSchedule
							saleOrderId={saleOrderId}
							segmentType={data.segmentType}
							onSetAlert={onSetAlert}
						/>
					</Box>
				</Box>
			)}

			{/* TODO: Dialog confirmación desactivación Sincro NetSuite — reactivar con bloque Sincro
			<Dialog
				open={modalOpen}
				onClose={handleCloseModal}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">{'Confirmar Desactivación'}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						¿Desactivar la sincronización con Netsuite? Esto impedirá la facturación y contabilización automática de la
						orden al finalizar el dia.
					</DialogContentText>
					<DialogContentText id="alert-dialog-description">
						Desactivar mientras trabajas en la orden puede ser prudente, pero reactivar a tiempo es crucial para su
						procesamiento.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={handleCloseModal}>
						Cancelar
					</Button>
					<Button variant="outlined" onClick={handleConfirmModal} autoFocus>
						Aceptar
					</Button>
				</DialogActions>
			</Dialog>
			*/}

			<ChangeSaleOrderSellerModal
				saleOrderId={data.id}
				open={openChangeSellerModal}
				onClose={() => setOpenChangeSellerModal(false)}
				agencyId={agencyIdOfMarketCountry}
				currentSellerUserId={data.createdById}
				onSaved={onUpdate}
			/>
		</Box>
	);
};
